import { useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DO_PAPEL,
  useAdicionarParticipante,
  useCandidatosAParticipante,
  useParticipantes,
  useRemoverParticipante,
  type PapelDoParticipante,
} from "../../nucleo/tickets-operacao";
import { Aviso, Botao, Cartao, Chip } from "../ui";

interface Props {
  ticketId: string;
  /** Console da equipe: mostra quem da GEOvendas acompanha e deixa incluir agentes. */
  interno: boolean;
  podeGerenciar: boolean;
}

/** Quem pediu junto e quem acompanha o chamado — e o formulário para incluir mais gente. */
export function ParticipantesDoChamado({ ticketId, interno, podeGerenciar }: Props) {
  const participantes = useParticipantes(ticketId);
  const candidatos = useCandidatosAParticipante(ticketId, podeGerenciar);
  const adicionar = useAdicionarParticipante(ticketId);
  const remover = useRemoverParticipante(ticketId);
  const [pessoaId, setPessoaId] = useState("");
  const [papel, setPapel] = useState<PapelDoParticipante>("seguidor");

  const escolhida = candidatos.data?.find((item) => item.id === pessoaId);
  // Agente da equipe só acompanha: a opção de solicitante não faz sentido para ele.
  const papelEfetivo: PapelDoParticipante = escolhida?.interno ? "seguidor" : papel;

  function incluir(evento: FormEvent) {
    evento.preventDefault();
    if (!pessoaId) return;
    adicionar.mutate({ usuario_id: pessoaId, papel: papelEfetivo }, { onSuccess: () => setPessoaId("") });
  }

  return (
    <Cartao titulo="Quem acompanha este chamado" className="mb-4" data-testid="participantes-do-chamado">
      <p className="nota-rodape mb-3">
        {interno
          ? "Quem acompanha recebe e-mail a cada resposta. Seguidores da equipe recebem também as notas internas."
          : "Quem acompanha recebe um e-mail a cada resposta da equipe."}
      </p>
      {participantes.isPending ? (
        <p role="status">Carregando participantes…</p>
      ) : participantes.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(participantes.error)}</Aviso>
      ) : (
        <ul className="lista-de-itens mb-3">
          {participantes.data.solicitante ? (
            <li data-testid="solicitante-principal">
              <strong>{participantes.data.solicitante.nome}</strong>{" "}
              <span className="nota-rodape">{participantes.data.solicitante.email}</span>{" "}
              <Chip estado="ok" semPonto>Solicitante principal</Chip>
            </li>
          ) : null}
          {participantes.data.participantes.map((item) => (
            <li className="flex flex-wrap items-center gap-2" key={item.usuario_id}>
              <span>
                <strong>{item.nome}</strong> <span className="nota-rodape">{item.email}</span>
              </span>
              <Chip estado={item.papel === "solicitante" ? "ok" : "neutro"} semPonto>
                {ROTULO_DO_PAPEL[item.papel]}
              </Chip>
              {item.interno ? <Chip estado="aguardando" semPonto>Equipe GEOvendas</Chip> : null}
              {podeGerenciar ? (
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  aria-label={`Retirar ${item.nome}`}
                  onClick={() => remover.mutate(item.usuario_id)}
                  disabled={remover.isPending}
                >
                  Retirar
                </Botao>
              ) : null}
            </li>
          ))}
          {participantes.data.participantes.length === 0 ? (
            <li className="nota-rodape">Ninguém mais acompanha este chamado.</li>
          ) : null}
        </ul>
      )}
      {remover.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(remover.error)}</Aviso> : null}

      {podeGerenciar ? (
        <form className="flex flex-wrap items-end gap-3" onSubmit={incluir} data-testid="incluir-participante">
          <label className="campo flex-1" htmlFor={`pessoa-do-chamado-${ticketId}`}>
            <span className="campo__rotulo">Pessoa a incluir</span>
            <select
              id={`pessoa-do-chamado-${ticketId}`}
              className="campo__controle"
              value={pessoaId}
              onChange={(evento) => setPessoaId(evento.target.value)}
            >
              <option value="">Escolha uma pessoa</option>
              {candidatos.data?.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.nome} · {item.email}
                  {item.interno ? " (equipe)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="campo" htmlFor={`papel-do-participante-${ticketId}`}>
            <span className="campo__rotulo">Como participa</span>
            <select
              id={`papel-do-participante-${ticketId}`}
              className="campo__controle"
              value={papelEfetivo}
              onChange={(evento) => setPapel(evento.target.value as PapelDoParticipante)}
              disabled={Boolean(escolhida?.interno)}
            >
              <option value="seguidor">{ROTULO_DO_PAPEL.seguidor}</option>
              <option value="solicitante">{ROTULO_DO_PAPEL.solicitante}</option>
            </select>
          </label>
          <Botao type="submit" disabled={!pessoaId || adicionar.isPending}>
            {adicionar.isPending ? "Incluindo…" : "Incluir pessoa"}
          </Botao>
        </form>
      ) : null}
      {candidatos.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(candidatos.error)}</Aviso> : null}
      {adicionar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(adicionar.error)}</Aviso> : null}
    </Cartao>
  );
}
